import * as React from 'preact/compat'
import ImageCrop from './ImageCrop'
import SVG from './SVG'
import styles from '../../css/logo-upload.pcss.json'
import { captureException, loadStylesheet } from "../common/helpers"
import type { VNode } from "preact";
import type { HTMLAttributes } from 'preact/compat'
import { sgAlert } from "../plugins/SgAlert"
import { getOrientation } from "../common/get-image-orientation";

declare global {
  interface Window {
    logoUploadInitialized: boolean
  }
}

const ORIENTATION_TO_ANGLE = {
  '3': 180,
  '6': 90,
  '8': -90,
}

interface LogoUploadProps {
  value?: string,
  name: string,
  placeholder?: string,
  note?: string,
  preview?: string | boolean,
  widthLimit?: number,
  heightLimit?: number,
  checkSize?: boolean,
  crop?: string,
  cropRound?: boolean,
  buttonClass?: string,
  buttonContent?: VNode<any>,
  onDone?: (data: { image: string, files: FileList, input: LogoUpload_react }) => void | string,
  noteClass?: string,
  inputOptions?: HTMLAttributes<HTMLInputElement>
}

interface LogoUploadState {
  fileName: string,
  imgData?: string,
  cropImg?: string,
  dragOver: boolean,
}

export default class LogoUpload_react extends React.Component<LogoUploadProps, LogoUploadState> {
  state: LogoUploadState = {
    fileName: '',
    imgData: this.props.value ?? undefined,
    dragOver: false,
  }

  fileInput = React.createRef<HTMLInputElement>()

  componentWillMount() {
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('logo-upload')
  }

  componentDidMount() {
    document.addEventListener('dragover', () => {
      this.setState({ dragOver: true })
    })
    document.addEventListener('dragleave', (e) => {
      if (e.target === document.body) {
        this.setState({ dragOver: false })
      }
    })
    document.addEventListener('drop', () => {
      this.setState({ dragOver: false })
    })
    window.dispatchEvent(new Event('logo-upload-initialized'))
    window.logoUploadInitialized = true
  }

  componentWillReceiveProps(nextProps: Readonly<LogoUploadProps>) {
    if (this.state.imgData !== nextProps.value) {
      this.setState({
        imgData: nextProps.value
      })
    }
  }

  readFile = (file: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader()
      fileReader.onload = (loadEvent) => {
        const img = document.createElement('img')
        img.onload = () => {
          if (this.props.crop) {
            resolve(loadEvent.target.result as string)
          } else if (this.checkSize(img)) {
            let w = 1920
            let h = 1080

            if (this.props.widthLimit) {
              w = this.props.widthLimit
            }

            if (this.props.heightLimit) {
              h = this.props.heightLimit
            }

            sgAlert(`Изображение слишком большое! Рекомендуемый максимальный размер — ${w}×${h} пикселей.`)
            reject()
          } else {
            resolve(loadEvent.target.result as string)
          }
        }
        img.src = loadEvent.target.result as string
      }
      fileReader.readAsDataURL(file)
    })
  }

  clear = () => {
    this.fileInput.current.value = ''
    this.setState({
      fileName: '',
      imgData: undefined,
    })
  }

  getRotatedImage = async (imageSrc: string, rotation: number = 0) => {
    const image = await ImageCrop.createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    const orientationChanged =
      rotation === 90 || rotation === -90 || rotation === 270 || rotation === -270
    if (orientationChanged) {
      // noinspection JSSuspiciousNameCombination
      canvas.width = image.height
      // noinspection JSSuspiciousNameCombination
      canvas.height = image.width
    } else {
      canvas.width = image.width
      canvas.height = image.height
    }

    ctx.translate(canvas.width / 2, canvas.height / 2)
    ctx.rotate((rotation * Math.PI) / 180)
    ctx.drawImage(image, -image.width / 2, -image.height / 2)

    return new Promise<string>((resolve) => {
      canvas.toBlob((file) => {
        resolve(URL.createObjectURL(file))
      }, 'image/png')
    })
  }

  doPreview = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget
    const value = input.value
    const file = input.files[0]
    const fileName = value.split('\\').pop()

    if (!(file instanceof Blob)) {
      this.setState({
        fileName
      })
      return
    }

    try {
      let imgData = await this.readFile(file)
      const orientation = await getOrientation(file)
      const rotation = ORIENTATION_TO_ANGLE[orientation]
      if (rotation) {
        imgData = await this.getRotatedImage(imgData, rotation)
      }

      if (this.props.crop) {
        this.crop(imgData, fileName)
      } else {
        this.setPreviewImage(imgData, fileName)
        if (this.props.onDone) {
          if (typeof this.props.onDone === 'function') {
            this.props.onDone({ image: imgData, files: input.files, input: this })
          } else if (typeof window[this.props.onDone] === 'function') {
            // @ts-ignore
            window[this.props.onDone]({ image: imgData, files: input.files, input: this })
          }
        }
      }
    } catch (e) {
      captureException('Logo Upload', e)
    }
  }

  setPreviewImage = (imgData: string, fileName: string, hideCrop = false) => {
    const state: Partial<LogoUploadState> = {
      fileName,
      imgData: imgData
    }

    if (hideCrop) {
      state.cropImg = undefined
    }

    this.setState(state)
    if (typeof this.props.preview === 'string') {
      const preview = document.querySelector<HTMLImageElement>(this.props.preview)
      if (preview) {
        if (preview.parentElement.tagName === 'PICTURE') {
          preview.parentElement.querySelectorAll('source').forEach(item => item.remove())
        }
        if (preview.hasAttribute('srcset')) {
          preview.removeAttribute('srcset')
        }
        preview.src = imgData
      }
    }
  }

  crop = (cropImg: string, fileName: string) => {
    this.setState({
      cropImg,
      fileName
    })
  }

  checkSize = (img: HTMLImageElement) => {
    if (!this.props.checkSize) {
      return false
    }

    const { widthLimit, heightLimit } = this.props

    if (widthLimit && img.width > widthLimit) {
      return true;
    }

    if (heightLimit && img.height > heightLimit) {
      return true;
    }

    return (img.width * img.height / 1000000) > 4
  }

  cropComplete = async (imgData: string, fileName?: string) => {
    if (!fileName) {
      fileName = this.state.fileName
    }
    if (fileName === '') {
      fileName = 'temp.png'
    }
    this.setPreviewImage(imgData, fileName, true)
    const imgBlob = await fetch(imgData).then(res => res.blob())
    const file = new File([imgBlob], fileName, { type: 'image/png' })
    const container = new DataTransfer()
    container.items.add(file)
    this.fileInput.current.files = container.files
    if (this.props.onDone) {
      if (typeof this.props.onDone === 'function') {
        this.props.onDone({ image: imgData, files: container.files, input: this })
      } else if (typeof window[this.props.onDone] === 'function') {
        // @ts-ignore
        window[this.props.onDone]({ image: imgData, files: container.files, input: this })
      }
    }
  }

  closeCrop = () => {
    this.setState({
      fileName: '',
      cropImg: undefined
    }, () => {
      this.fileInput.current.value = ''
    })
  }

  render() {
    const { name, placeholder, note, preview, crop, cropRound } = this.props
    const { fileName, imgData, cropImg, dragOver } = this.state

    let buttonText = placeholder
    if (dragOver) {
      buttonText = 'Бросай файл сюда!'
    } else if (fileName !== '') {
      buttonText = fileName
    }

    const buttonClass = this.props.buttonClass ?? styles['upload-button']
    const noteClass = this.props.noteClass ?? 'form-note'
    const buttonContent = this.props.buttonContent ?? <>
      <SVG i='sg-new/upload' adaptive/>
      <span className={styles['text']}>{buttonText}</span>
    </>

    return <>
      {imgData && preview === true && <img alt="" src={imgData} className={styles['preview']} style=""/>}
      <div className={buttonClass} data-input="file">
        {buttonContent}
        <input
          type="file"
          ref={this.fileInput}
          name={name}
          accept="image/*"
          onChange={this.doPreview}
          {...this.props.inputOptions}
        />
      </div>
      {typeof note === 'string' && note.length > 0 &&
        <div className={noteClass} dangerouslySetInnerHTML={{ __html: note }}/>
      }
      {crop && cropImg && <ImageCrop
        image={cropImg}
        round={cropRound}
        hideGrid={cropRound}
        size={crop.split(/[Xx:×]/).map(i => parseInt(i))}
        onCropComplete={this.cropComplete}
        onClose={this.closeCrop}
      />}
    </>;
  }
}
